import isEmpty from 'vanillas/isEmpty';
import isObject from 'vanillas/isObject';
import { getCvAccessToken } from '@carvana/core-auth';

import type { Resource } from './resources';

const BASE_API_URL = `${process.env.CARVANA_APP_TRADES_API}/api` as const;
const BASE_API_URL_V1 = `${BASE_API_URL}/v1`;
const BASE_API_URL_V2 = `${BASE_API_URL}/v2`;
const BASE_API_URL_V3 = `${BASE_API_URL}/v3`;
const BASE_API_URL_V4 = `${BASE_API_URL}/v4`;
const BASE_API_URL_V5 = `${BASE_API_URL}/v5`;
const BASE_URL = `${process.env.CARVANA_APP_BASE_URL}`;

export enum BaseAPI {
  Trades = 'trades',
  QuestionsAPI = 'questions-api'
}

const getApiVersionBase = (api: BaseAPI, version: number) => {
  switch (api) {
    case BaseAPI.Trades:
      switch (version) {
        case 1:
          return BASE_API_URL_V1;
        case 2:
          return BASE_API_URL_V2;
        case 3:
          return BASE_API_URL_V3;
        case 4:
          return BASE_API_URL_V4;
        case 5:
          return BASE_API_URL_V5;
        default:
          return BASE_API_URL_V1;
      }
    default:
      return null;
  }
};

const getApiPathHelper = (
  api: BaseAPI,
  controller: string,
  resource: string,
  token: string,
  version: number
) => {
  const baseApi = getApiVersionBase(api, version);
  const optionalPath = !isEmpty(token) ? '/limited' : '';
  const base = `${baseApi}/${controller}`;
  const withResource = `${base}${!isEmpty(resource) ? '/' : ''}${resource}`;
  return `${withResource}${optionalPath}`;
};

const getTradesApiPathHelper = (
  controller: string,
  resource: string,
  token: string,
  version: number
) => getApiPathHelper(BaseAPI.Trades, controller, resource, token, version);

const getAuthorization = () => {
  const token = getCvAccessToken();

  if (!token) {
    return '';
  }

  return `Bearer ${token}`;
};

const getHeaders = (isForm?: boolean, contentType?: string) => {
  const CONTENT_TYPE = 'Content-Type';
  const header = {
    headers: {
      Authorization: getAuthorization(),
      [CONTENT_TYPE]: undefined as string
    }
  };

  if (!contentType) {
    header.headers[CONTENT_TYPE] = 'application/json';
  } else {
    header.headers[CONTENT_TYPE] = contentType;
  }

  if (isForm) {
    header.headers[CONTENT_TYPE] = 'multipart/form-data';
  }

  return header;
};

const getTradesApiPathv3 = (controller, resource, token?) =>
  getTradesApiPathHelper(controller, resource, token, 3);

const getErrorCode = (error?: { response?: { status?: number } }) =>
  error?.response?.status ?? null;

const getErrorMessage = (error?: { message: any; response?: any }): string => {
  if (!error) {
    return '';
  }

  if (error.response) {
    const responseData = error.response.data;

    if (typeof responseData === 'string') return responseData;

    if (isObject(responseData)) {
      const { message } = responseData;
      if (typeof message === 'string') return message;

      const { errorMessage } = responseData;
      if (errorMessage) return errorMessage;
    }
    return responseData;
  }
  return error.message;
};

const buildUrl = (parts: Resource, token?: string) =>
  getApiPathHelper(
    parts.api ?? BaseAPI.Trades,
    parts.controller,
    parts.resource,
    token || '',
    parts.version
  );

export {
  BASE_API_URL,
  BASE_URL,
  buildUrl,
  getErrorCode,
  getErrorMessage,
  getHeaders,
  getTradesApiPathv3
};
